import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { onboardingApi } from '../services/onboardingApi';
import { OnboardingProject, CreateProjectRequest } from '@/types/onboarding';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { CalendarIcon, Building2, Users, CreditCard, AlertCircle, CheckCircle2, Upload, Image, Lightbulb, ArrowRight } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface OutletContext {
  project: OnboardingProject | null;
  setProject: (project: OnboardingProject | null) => void;
  loadProject: () => Promise<void>;
}

const Stage1ProjectInitiation = () => {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const { currentWorkspace } = useWorkspace();
  const { toast } = useToast();
  const { project, setProject } = useOutletContext<OutletContext>();

  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [importedCounts, setImportedCounts] = useState({ properties: 0, units: 0, accounts: 0 });
  const [formData, setFormData] = useState<CreateProjectRequest>({
    project_name: '',
    customer_name: '',
    customer_contact_name: '',
    customer_contact_email: '',
    target_go_live_date: undefined,
    estimated_properties: 0,
    estimated_units: 0,
    estimated_accounts: 0,
    project_owner_id: '',
    assigned_team: [],
    notes: '',
  });
  const [targetDate, setTargetDate] = useState<Date | undefined>(undefined);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  useEffect(() => {
    if (project && projectId && projectId !== 'new') {
      loadImportedCounts();
    }
  }, [project, projectId]);

  const loadImportedCounts = async () => {
    if (!project || projectId === 'new') return;
    try {
      const counts = await onboardingApi.getImportedCounts(project.id);
      setImportedCounts(counts);
      
      // Auto-update formData if estimated counts are less than actual active counts
      // This ensures the form always shows counts >= actual imported/active entities
      setFormData(prev => ({
        ...prev,
        estimated_properties: Math.max(prev.estimated_properties || 0, counts.properties),
        estimated_units: Math.max(prev.estimated_units || 0, counts.units),
        estimated_accounts: Math.max(prev.estimated_accounts || 0, counts.accounts),
      }));
      
      // Also refresh project data to get updated estimated counts from database
      if (projectId && projectId !== 'new') {
        const updatedProject = await onboardingApi.getProject(projectId);
        if (updatedProject) {
          setProject(updatedProject);
        }
      }
    } catch (error) {
      console.error('Failed to load imported counts:', error);
    }
  };

  const validationErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    if (formData.estimated_properties < importedCounts.properties) {
      errors.estimated_properties = `Cannot be less than ${importedCounts.properties} (already imported)`;
    }
    if (formData.estimated_units < importedCounts.units) {
      errors.estimated_units = `Cannot be less than ${importedCounts.units} (already imported)`;
    }
    if (formData.estimated_accounts < importedCounts.accounts) {
      errors.estimated_accounts = `Cannot be less than ${importedCounts.accounts} (already imported)`;
    }
    return errors;
  }, [formData, importedCounts]);

  useEffect(() => {
    if (project) {
      setFormData({
        project_name: project.customer_name,
        customer_name: project.customer_name,
        customer_contact_name: project.customer_contact_name || '',
        customer_contact_email: project.customer_contact_email || '',
        target_go_live_date: project.target_go_live_date || undefined,
        estimated_properties: project.estimated_properties,
        estimated_units: project.estimated_units,
        estimated_accounts: project.estimated_accounts,
        project_owner_id: project.project_owner_id || '',
        assigned_team: project.assigned_team || [],
        notes: project.notes || '',
      });
      if (project.target_go_live_date) {
        setTargetDate(new Date(project.target_go_live_date));
      }
      if (project.logo_url) {
        setLogoPreview(project.logo_url);
      }
    }
  }, [project]);

  const handleLogoUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !project) return;

    setUploadingLogo(true);
    try {
      const url = await onboardingApi.uploadCustomerLogo(project.id, file);
      setLogoPreview(url);
      toast({ title: 'Logo uploaded', description: 'Customer logo has been updated' });
    } catch (error: any) {
      toast({ title: 'Upload failed', description: error.message, variant: 'destructive' });
    } finally {
      setUploadingLogo(false);
    }
  }, [project, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (Object.keys(validationErrors).length > 0) {
      toast({ title: 'Validation Error', description: 'Please fix the errors', variant: 'destructive' });
      return;
    }
    
    if (!currentWorkspace) {
      toast({ title: 'Error', description: 'No workspace selected', variant: 'destructive' });
      return;
    }

    try {
      setLoading(true);

      if (projectId === 'new') {
        const newProject = await onboardingApi.createProject(currentWorkspace.id, {
          ...formData,
          project_name: formData.customer_name,
          target_go_live_date: targetDate ? format(targetDate, 'yyyy-MM-dd') : undefined,
        });
        setProject(newProject);
        toast({ title: 'Customer Created', description: 'Onboarding customer created successfully' });
        navigate(`/w/${currentWorkspace.id}/dashboard/tum/onboarding/project/${newProject.id}/stage-2`);
      } else if (project) {
        const updatedProject = await onboardingApi.updateProject(project.id, {
          project_name: formData.customer_name,
          customer_name: formData.customer_name,
          customer_contact_name: formData.customer_contact_name,
          customer_contact_email: formData.customer_contact_email,
          target_go_live_date: targetDate ? format(targetDate, 'yyyy-MM-dd') : undefined,
          estimated_properties: formData.estimated_properties,
          estimated_units: formData.estimated_units,
          estimated_accounts: formData.estimated_accounts,
          notes: formData.notes,
        });
        setProject(updatedProject);
        toast({ title: 'Customer Updated', description: 'Customer details updated' });
        navigate(`/w/${currentWorkspace.id}/dashboard/tum/onboarding/project/${project.id}/stage-2`);
      }
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Failed to save', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Stage 1: Customer Initiation</h2>
          <p className="text-sm text-muted-foreground">Set up customer details and timeline</p>
        </div>
        {projectId !== 'new' && (
          <Badge variant="outline" className="text-xs">
            Step 1 of 7
          </Badge>
        )}
      </div>

      {/* Quick Tips Card */}
      <Card className="bg-blue-50/50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Lightbulb className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-blue-900">Quick Tips</p>
              <ul className="text-blue-800 mt-1 space-y-0.5 text-xs">
                <li>• Customer name will be used across all stages</li>
                <li>• Estimates help track progress but can be adjusted later</li>
                <li>• Set a realistic go-live date based on data volume</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Customer Details + Logo */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Customer Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-4">
              {/* Logo Upload */}
              {projectId !== 'new' && (
                <div className="flex-shrink-0">
                  <Label className="text-xs text-muted-foreground mb-1.5 block">Logo</Label>
                  <label className="cursor-pointer block">
                    <div className={cn(
                      "w-20 h-20 rounded-lg border-2 border-dashed flex items-center justify-center overflow-hidden transition-colors",
                      uploadingLogo ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                    )}>
                      {logoPreview ? (
                        <img src={logoPreview} alt="Logo" className="w-full h-full object-cover" />
                      ) : (
                        <div className="text-center text-muted-foreground">
                          <Image className="h-6 w-6 mx-auto" />
                          <span className="text-[10px]">Upload</span>
                        </div>
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      disabled={uploadingLogo}
                    />
                  </label>
                </div>
              )}

              {/* Name Fields */}
              <div className="flex-1 space-y-3">
                <div>
                  <Label htmlFor="customer_name" className="text-xs">Customer Name *</Label>
                  <Input
                    id="customer_name"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    placeholder="e.g., Allegiant-Carter Management LLC"
                    className="h-9"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="customer_contact_name" className="text-xs">Contact Name</Label>
                    <Input
                      id="customer_contact_name"
                      value={formData.customer_contact_name}
                      onChange={(e) => setFormData({ ...formData, customer_contact_name: e.target.value })}
                      placeholder="Contact name"
                      className="h-9"
                    />
                  </div>
                  <div>
                    <Label htmlFor="customer_contact_email" className="text-xs">Contact Email</Label>
                    <Input
                      id="customer_contact_email"
                      type="email"
                      value={formData.customer_contact_email}
                      onChange={(e) => setFormData({ ...formData, customer_contact_email: e.target.value })}
                      placeholder="email@example.com"
                      className="h-9"
                    />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Portfolio Size - Compact */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Portfolio Size</CardTitle>
            <CardDescription className="text-xs">Estimated counts (can be updated later)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              {/* Properties */}
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-1.5">
                  <Building2 className="h-3.5 w-3.5" />
                  Properties
                </Label>
                <Input
                  type="number"
                  min={importedCounts.properties}
                  value={formData.estimated_properties}
                  onChange={(e) => setFormData({ ...formData, estimated_properties: parseInt(e.target.value) || 0 })}
                  className={cn("h-9", validationErrors.estimated_properties && "border-red-500")}
                />
                {importedCounts.properties > 0 && (
                  <p className="text-[10px] text-muted-foreground">{importedCounts.properties} imported</p>
                )}
                {validationErrors.estimated_properties && (
                  <p className="text-[10px] text-red-600">{validationErrors.estimated_properties}</p>
                )}
              </div>

              {/* Units */}
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5" />
                  Units
                </Label>
                <Input
                  type="number"
                  min={importedCounts.units}
                  value={formData.estimated_units}
                  onChange={(e) => setFormData({ ...formData, estimated_units: parseInt(e.target.value) || 0 })}
                  className={cn("h-9", validationErrors.estimated_units && "border-red-500")}
                />
                {importedCounts.units > 0 && (
                  <p className="text-[10px] text-muted-foreground">{importedCounts.units} imported</p>
                )}
              </div>

              {/* Accounts */}
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-1.5">
                  <CreditCard className="h-3.5 w-3.5" />
                  Accounts
                </Label>
                <Input
                  type="number"
                  min={importedCounts.accounts}
                  value={formData.estimated_accounts}
                  onChange={(e) => setFormData({ ...formData, estimated_accounts: parseInt(e.target.value) || 0 })}
                  className={cn("h-9", validationErrors.estimated_accounts && "border-red-500")}
                />
                {importedCounts.accounts > 0 && (
                  <p className="text-[10px] text-muted-foreground">{importedCounts.accounts} imported</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Timeline & Notes */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Timeline & Notes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs">Target Go-Live Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn("w-full justify-start text-left font-normal h-9", !targetDate && "text-muted-foreground")}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {targetDate ? format(targetDate, 'PPP') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={targetDate} onSelect={setTargetDate} initialFocus />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label htmlFor="notes" className="text-xs">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional notes..."
                className="min-h-[60px] resize-none"
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button type="submit" disabled={loading} className="gap-2">
            {loading ? 'Saving...' : projectId === 'new' ? 'Create & Continue' : 'Save & Continue'}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Stage1ProjectInitiation;
